import {
  NgModule,
  ApplicationRef,
  Provider,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { OwlDateTimeModule, OwlNativeDateTimeModule, OWL_DATE_TIME_FORMATS } from '@danielmoncada/angular-datetime-picker';
import { MatNativeDateModule } from '@angular/material/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MetabaseServices } from 'src/services/metabaseServices';
import * as Sentry from '@sentry/angular';
import { MatDialogModule } from '@angular/material/dialog';
import VersionInfo from '../../package.json';
import { LocalStorage } from '../services/local-storage-service';
import { HelperService } from '../services/helper-service';
import { WindowRefService } from '../services/window-ref-service';
import { AppComponent } from './app.component';
import { LoginModule } from './login';
import { LogoutModule } from './logout';
import { DirectivesModule } from '../components/directives.module';
import { ChatModule } from './chat';
import { UserModule } from './user';
import { SchedulerModule } from './scheduler';
import { LanguageStringModule } from './language-string';
import { ProductModule } from './product';
import { CouriersModule } from './couriers';
import { DoctorModule } from './doctor';
import { TreeModule } from './tree';
import { AppConfig } from './app.config';
import { EasyAccessModule } from './easyAccess';
import { OrderModule } from './order';
import { CallsLogModule } from './calls';
import { ClipboardService } from '../services/copyToClipboard-service';
import { CompareImageProgressModule } from './compare-progress';
import { environment } from '../environments/environment';
import { InputHelperService } from '../components/input/input-helper';
import { UploadFileService } from '../services/uploadFileService/uploadFile.service';
import { InstantCheckupModule } from './instant-checkup';
import { UserFollowUpReportModule } from './follow-up-report';
import { PatientModule } from './patient';
import { TimeService } from '../services/time-service';
import { EcomModule } from './ecom';
import { AnalyticsModule } from './analytics';
import { VoiceRecognitionService } from '../services/voice-recognition-service/voice-recognition.service';
import { CannedResponseModule } from './canned-response';
import { ValidationService } from '../services/validation-service';
import { LanguageVoiceModule } from './language-voice';
import { MarketingModule } from './marketing';
import { Broadcaster } from '../components/broadcaster';
import { SKUModule } from './sku';
import { ActivityTrackerDirective } from '../components/activity-tracker/activity-tracker.directive';
import { ReminderModule } from './reminder';
import { ExperimentModule } from './experiment';
import { DashboadModule } from './dashboard/dashboad.module';
import { CartModule } from './cart';
import { SalesModule } from './sales/sales.module';
import { SBARService } from '../services/sbar-service';
import { OfflineDoctorScheduleModule } from './offline-doctor-schedule';
import { IframeService } from '../services/iframe-service';
import { RegimenService } from '../services/regimen-service';
import { UserInstructionsLLMModule } from './user-instructions-llm';
import { ProductCollectionsModule } from './product/product-collections';

export const MY_NATIVE_FORMATS = {
  fullPickerInput: { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' },
};

const providers: Provider[] = [
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
  HelperService,
  ValidationService,
  LocalStorage,
  ClipboardService,
  InputHelperService,
  UploadFileService,
  TimeService,
  VoiceRecognitionService,
  MetabaseServices,
  Broadcaster,
  SBARService,
  IframeService,
  RegimenService,
  { provide: OWL_DATE_TIME_FORMATS, useValue: MY_NATIVE_FORMATS },
  { provide: AppConfig, useValue: AppConfig },
];

const appRoutes: Routes = [
  { path: '', redirectTo: '/chats/open-tickets', pathMatch: 'full' },
  {
    path: 'productCollections',
    loadChildren: (): any => ProductCollectionsModule,
  },
];

let sentryEnvironment = '';
const windowRefService = new WindowRefService();
if (windowRefService.nativeWindow.location.hostname.startsWith('app.cureskin.com')) {
  sentryEnvironment = 'production';
}
if (windowRefService.nativeWindow.location.hostname.startsWith('test-app.cureskin.com')) {
  sentryEnvironment = 'development';
}
if (windowRefService.nativeWindow.location.hostname.startsWith('staging-app.cureskin.com')) {
  sentryEnvironment = 'staging';
}

if (sentryEnvironment) {
  Sentry.init({
    environment: sentryEnvironment,
    release: `doctor@${VersionInfo.version}`,
    dsn: 'https://02e4d62b1dfed3dbf395750bb427f141@sentry.cureskin.com/4',
    integrations: [
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

@NgModule({ declarations: [AppComponent, ActivityTrackerDirective],
    bootstrap: [AppComponent], imports: [BrowserModule,
        MatNativeDateModule,
        DragDropModule,
        RouterModule.forRoot(appRoutes, { enableTracing: !environment.production }),
        DirectivesModule,
        LoginModule,
        UserInstructionsLLMModule,
        LogoutModule,
        ChatModule,
        TreeModule,
        ProductModule,
        CouriersModule,
        DoctorModule,
        UserModule,
        EasyAccessModule,
        BrowserAnimationsModule,
        OrderModule,
        SchedulerModule,
        CallsLogModule,
        CompareImageProgressModule,
        LanguageStringModule,
        LanguageVoiceModule,
        InstantCheckupModule,
        UserFollowUpReportModule,
        PatientModule,
        EcomModule,
        AnalyticsModule,
        CannedResponseModule,
        MarketingModule,
        ReminderModule,
        SKUModule,
        ExperimentModule,
        DashboadModule,
        CartModule,
        MatDialogModule,
        SalesModule,
        OfflineDoctorScheduleModule,
        ProductCollectionsModule],
        providers: [provideHttpClient(withInterceptorsFromDi()), providers] })
export class AppModule {
  appRef: ApplicationRef;
  static parameters: any = [ApplicationRef];

  constructor(appRef: ApplicationRef) {
    this.appRef = appRef;
  }
}
